import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, _setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locale, setLocale] = useState(() => localStorage.getItem('locale') || 'en');

  // Iter 72 — wrap setUser so the localStorage session hint stays in sync with
  // the React user state. Any call site that does setUser(data) on a successful
  // login or setUser(null) on logout/expiry now gets the hint flipped for free.
  const setUser = useCallback((u) => {
    _setUser(u);
    try {
      if (u) localStorage.setItem('pg_session_hint', '1');
      else   localStorage.removeItem('pg_session_hint');
    } catch (_) {}
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      // Iter 72 — record a "session hint" so future page loads on public marketing
      // routes can skip the /auth/me probe entirely (no console 401 noise) while
      // still hydrating returning users.
      try { localStorage.setItem('pg_session_hint', '1'); } catch (_) {}
    } catch {
      setUser(null);
      try { localStorage.removeItem('pg_session_hint'); } catch (_) {}
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Apply persisted theme variant + brand color overrides (set by admin via Settings).
    api.get('/clinic/settings').then(r => {
      const s = r.data || {};
      const theme = s.theme_variant;
      if (theme && theme !== 'default') {
        document.documentElement.setAttribute('data-theme', theme);
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
      // Optional per-tenant brand color override — applied as inline CSS variable
      // so it wins over the data-theme preset variables.
      if (s.brand_primary_color) {
        document.documentElement.style.setProperty('--brand', s.brand_primary_color);
        document.documentElement.style.setProperty('--brand-hover', s.brand_primary_color);
      } else {
        document.documentElement.style.removeProperty('--brand');
        document.documentElement.style.removeProperty('--brand-hover');
      }
      if (s.brand_accent_color) {
        document.documentElement.style.setProperty('--accent', s.brand_accent_color);
        document.documentElement.style.setProperty('--accent-hover', s.brand_accent_color);
      } else {
        document.documentElement.style.removeProperty('--accent');
        document.documentElement.style.removeProperty('--accent-hover');
      }
      // Per-tenant favicon swap. Falls back to /favicon.svg when blank.
      try {
        const iconHref = s.clinic_favicon_url || '/favicon.svg';
        let link = document.querySelector("link[rel='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        // For data: URLs the browser infers the type automatically.
        link.href = iconHref;
      } catch (_) { /* ignore — favicon is non-critical */ }
    }).catch(() => {});
    // If returning from Google OAuth callback (hash present), skip /me; AuthCallback will handle.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Skip the /auth/me probe on truly-stateless public routes — those pages
    // either handle their own session (magic-link redeem, OAuth callback) or
    // do not need user state at all (password reset, waitlist response). This
    // also kills the routine 401 console noise on those pages.
    // Iter 72 — also skip the probe on purely marketing / public routes when
    // we have no signal of an existing session (cookie / localStorage flag set
    // on successful login). A logged-in user navigating back to the landing
    // page keeps their flag and still hydrates.
    const path = window.location.pathname;
    const SKIP_ME_PROBE = [
      '/onboard/magic/',
      '/reset-password/',
      '/forgot-password',
      '/waitlist/respond/',
      '/auth-callback',
      '/share/progress/',
      '/refer/',
      '/gp/preferences/',
    ];
    if (SKIP_ME_PROBE.some((p) => path.startsWith(p))) {
      setLoading(false);
      return;
    }
    // Iter 72 — also skip on public marketing pages when there is no
    // "previously-signed-in" hint. Login + magic-redeem flip this flag.
    const PUBLIC_MARKETING = ['/', '/login', '/register', '/clinics', '/start-clinic', '/help', '/faq'];
    const isPublicMarketing = PUBLIC_MARKETING.includes(path) || path.startsWith('/c/') || path.startsWith('/legal/');
    const hasSessionHint = localStorage.getItem('pg_session_hint') === '1';
    if (isPublicMarketing && !hasSessionHint) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const setLocalePersist = (l) => {
    setLocale(l);
    localStorage.setItem('locale', l);
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (e) {}
    setUser(null);
    try { localStorage.removeItem('pg_session_hint'); } catch (_) {}
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, refreshUser: checkAuth, logout, locale, setLocale: setLocalePersist }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
